import { Outcome } from "@ethossoftworks/outcome"
import { compare, SortOrder } from "@lib/Comparable"
import {
    arrayToObject,
    boolOrDefault,
    nullNumOrDefault,
    nullStringOrDefault,
    numOrDefault,
    stringOrDefault,
} from "@lib/TypeUtil"
import { AssetCategory } from "@model/assets/AssetCategory"
import { AssetClass } from "@model/assets/AssetClass"
import { AssetMake } from "@model/assets/AssetMake"
import { AssetModel } from "@model/assets/AssetModel"
import { AssetType } from "@model/assets/AssetType"
import { TimeZone } from "@model/assets/TimeZone"
import { Company } from "@model/company/Company"
import { CompanyAlias, defaultCompanyAlias } from "@model/company/CompanyAlias"
import { ThresholdSettings, WorkOrderAutomationSettings, WorkOrderRequiredFields } from "@model/company/CompanySettings"
import { Contact, labelForContact } from "@model/contacts/Contact"
import { District } from "@model/district/District"
import { Group } from "@model/group/Group"
import { ApiServiceQuote, ApiServiceQuoteVendor, ServiceQuoteVendor } from "@model/serviceQuotes/ServiceQuote"
import {
    newServiceSubType,
    ServiceCode,
    ServiceRequestType,
    ServiceSchedule,
    ServiceScheduleType,
    ServiceSubType,
} from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { Step } from "@model/Step"
import { newThresholdGroup, Threshold, ThresholdGroup, ThresholdType, ThresholdUnit } from "@model/Threshold"
import { CompanyUrgency, Urgency } from "@model/Urgency"
import { CBACode } from "@model/workOrders/CbaCode"
import { WorkRequestType } from "@model/workRequests/WorkRequest"
import { ApiService, HttpMethod, nullableDateFromApi } from "@service/ApiService"
import { UserCacheModule } from "@service/cache/UserCacheModule"
import { CompanyService } from "./CompanyService"

export class MainCompanyService implements CompanyService {
    constructor(private apiService: ApiService, private userCacheModule: UserCacheModule) {}

    async fetchCbaCodes(): Promise<Outcome<CBACode[]>> {
        const path = "/core-api/CBACode"
        let response: any = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map((x) => CBACodeFromApi(x)))
    }

    async fetchContacts(): Promise<Outcome<Contact[]>> {
        const path = "/core-api/Person"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(contactFromApi)
                .sort((a, b) => compare(labelForContact(a), labelForContact(b), SortOrder.Asc))
        )
    }

    async fetchMechanics(): Promise<Outcome<Contact[]>> {
        const path = "/core-api/Mechanic/GetMechanics"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Post, path: path, body: {} })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(contactFromApi)
                .sort((a, b) => compare(labelForContact(a), labelForContact(b), SortOrder.Asc))
        )
    }

    async updateContact(contact: Contact): Promise<Outcome<Contact>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Patch,
            path: `/core-api/Person/${contact.id}`,
            body: contactToApi(contact),
        })

        if (response.isError()) return response
        return Outcome.ok(contactFromApi(response.value))
    }

    async fetchCompanyLogo(companyId: number): Promise<Outcome<string>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Get,
            path: "/core-api/Company/GetCompanyLogo",
            query: { companyId: [companyId] },
        })
        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        return Outcome.ok(response.value[0])
    }

    async fetchSubCompanies(): Promise<Outcome<Company[]>> {
        const path = "/core-api/Company/SubCompaniesInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(
                    (it): Company => ({
                        id: numOrDefault(it.companyId, 0),
                        name: stringOrDefault(it.companyName, ""),
                        parentId: numOrDefault(it.parentCompanyID, 0),
                        logoUrl: it.logoUrl,
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAssetMakes(): Promise<Outcome<AssetMake[]>> {
        const path = "/core-api/AssetMake/AssetMakesInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(
                    (it): AssetMake => ({
                        id: numOrDefault(it.assetMakeID, 0),
                        name: stringOrDefault(it.name, "").trim(),
                        description: stringOrDefault(it.description, ""),
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAssetModels(): Promise<Outcome<AssetModel[]>> {
        const path = "/core-api/AssetModel/AssetModelsInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(
                    (it): AssetModel => ({
                        id: numOrDefault(it.assetModelID, 0),
                        name: stringOrDefault(it.name, "").trim(),
                        makeId: numOrDefault(it.assetMakeID, 0),
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAssetTypes(): Promise<Outcome<AssetType[]>> {
        const path = "/core-api/AssetType/AssetTypesInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(
                    (it): AssetType => ({
                        id: numOrDefault(it.assetTypeID, 0),
                        name: stringOrDefault(it.name, "").trim(),
                        description: stringOrDefault(it.description, ""),
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAssetCategories(): Promise<Outcome<AssetCategory[]>> {
        const path = "/core-api/AssetCategory/AssetCategoriesInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value
                .map(
                    (it): AssetCategory => ({
                        id: numOrDefault(it.assetCategoryID, 0),
                        typeId: numOrDefault(it.assetTypeID, 0),
                        name: stringOrDefault(it.name, "").trim(),
                        description: stringOrDefault(it.description, ""),
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAssetClasses(): Promise<Outcome<AssetClass[]>> {
        const path = "/core-api/AssetClass/AssetClassesInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)
        return Outcome.ok(
            response.value
                .map(
                    (it): AssetClass => ({
                        id: numOrDefault(it.assetClassID, 0),
                        categoryId: numOrDefault(it.assetCategoryID, 0),
                        name: stringOrDefault(it.name, "").trim(),
                        description: stringOrDefault(it.description, ""),
                    })
                )
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchGroups(): Promise<Outcome<Group[]>> {
        const path = "/core-api/Group/GroupsInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map(groupFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc)))
    }

    async fetchDistricts(): Promise<Outcome<District[]>> {
        const path = "/core-api/District/DistrictsInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map(districtFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc)))
    }

    async fetchSubDistricts(): Promise<Outcome<District[]>> {
        const path = "/core-api/District/SubDistrictsInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map(districtFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc)))
    }

    async fetchUnits(): Promise<Outcome<District[]>> {
        const path = "/core-api/District/UnitsInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map(districtFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc)))
    }

    async fetchSitesInUse(): Promise<Outcome<Site[]>> {
        const path = "/core-api/Site/SiteInUse"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map(siteFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc)))
    }
    async fetchAllSites(): Promise<Outcome<Site[]>> {
        const path = "/core-api/Site?Active=true&PageSize=10000"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Get, path: path })
        }
        if (!ApiService.isValidPagedResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(
            response.value.results.map(siteFromObj).sort((a, b) => compare(a.name, b.name, SortOrder.Asc))
        )
    }

    async fetchAliases(companyId: number): Promise<Outcome<Record<CompanyAlias, string>>> {
        const path = "/core-api/CompanyAlias"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Get, path: path })
        }
        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        const aliases = response.value.find((x) => (x.companyID = companyId))
        return Outcome.ok(
            arrayToObject(Object.values(CompanyAlias), (key) => [key, aliases[key] ?? defaultCompanyAlias(key)])
        )
    }

    async fetchServiceSubTypes(): Promise<Outcome<Record<ServiceRequestType, ServiceSubType>>> {
        const path = "/core-api/ServiceSubType"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Get, path: path })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        const value = response.value

        return Outcome.ok(
            arrayToObject(Object.values(ServiceRequestType), (it) => [it, serviceSubTypeFromResponse(it, value)])
        )
    }

    async updateServiceSubTypes(
        serviceSubTypes: Record<ServiceRequestType, ServiceSubType>
    ): Promise<Outcome<Record<ServiceRequestType, ServiceSubType>>> {
        const responses = await Promise.all(
            Object.values(serviceSubTypes).map(async (type) => {
                const response = await this.apiService.makeRequest({
                    method: HttpMethod.Put,
                    path: `/core-api/ServiceSubType`,
                    query: {
                        id: type.id,
                    },
                    body: {
                        alias: type.alias,
                    },
                })
                if (!ApiService.isValidObjectResponse(response)) return Outcome.error(response)
                return response
            })
        )

        if (responses.some((it) => it.isError())) return Outcome.error(undefined)

        return Outcome.ok(
            arrayToObject(Object.values(ServiceRequestType), (type) => [
                type,
                serviceSubTypeFromResponse(
                    type,
                    responses.map((it) => (it.isOk() ? it.value : newServiceSubType(type)))
                ),
            ])
        )
    }

    async fetchWorkOrderAutomationSettings(): Promise<Outcome<WorkOrderAutomationSettings>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Get,
            path: "/core-api/WorkOrderAutomation",
        })
        if (!ApiService.isValidObjectResponse(response)) return Outcome.error(response)

        return Outcome.ok(workOrderAutomationFromApi(response.value))
    }

    async updateWorkOrderAutomationSettings(
        settings: WorkOrderAutomationSettings
    ): Promise<Outcome<WorkOrderAutomationSettings>> {
        const method = settings.id === null ? HttpMethod.Post : HttpMethod.Put
        const path =
            settings.id === null ? "/core-api/WorkOrderAutomation" : `/core-api/WorkOrderAutomation/${settings.id}`
        const response = await this.apiService.makeRequest({ method, path, body: workOrderAutomationToApi(settings) })
        if (!ApiService.isValidObjectResponse(response)) return Outcome.error(response)

        return Outcome.ok(workOrderAutomationFromApi(response.value))
    }

    // Deliberately not routed through UserCacheModule: a saved selection has to be visible to the
    // very next Work Order completion, and the API already resolves the company hierarchy for us.
    async fetchWorkOrderRequiredFields(companyId: number): Promise<Outcome<WorkOrderRequiredFields>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Get,
            path: "/core-api/WorkOrderRequiredFields",
            query: { companyId: [companyId] },
        })
        if (!ApiService.isValidObjectResponse(response)) return Outcome.error(response)

        return Outcome.ok(workOrderRequiredFieldsFromApi(response.value))
    }

    async updateWorkOrderRequiredFields(
        companyId: number,
        requiredFields: WorkOrderRequiredFields
    ): Promise<Outcome<WorkOrderRequiredFields>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Put,
            path: "/core-api/WorkOrderRequiredFields",
            query: { companyId: [companyId] },
            body: workOrderRequiredFieldsToApi(requiredFields),
        })
        if (!ApiService.isValidObjectResponse(response)) return Outcome.error(response)

        return Outcome.ok(workOrderRequiredFieldsFromApi(response.value))
    }

    async fetchThresholdSettings(companyId: number): Promise<Outcome<ThresholdSettings>> {
        const path = "/core-api/UpcomingThreshold"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: path,
                query: { companyId: [companyId] },
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok({
            [WorkRequestType.Inspection]: thresholdGroupFromObj(response.value.find((it) => it.serviceTypeID === 2)),
            [WorkRequestType.Preventative]: thresholdGroupFromObj(response.value.find((it) => it.serviceTypeID === 1)),
            [WorkRequestType.Service]: thresholdGroupFromObj(response.value.find((it) => it.serviceTypeID === 3)),
        })
    }

    async updateThresholdSettings(thresholds: ThresholdSettings): Promise<Outcome<ThresholdSettings>> {
        const valueForThresholdType = (threshold: Threshold, type: ThresholdUnit): number | null =>
            threshold.unit === type ? threshold.value : null

        const serviceIdForGroupType = (
            type: WorkRequestType.Preventative | WorkRequestType.Inspection | WorkRequestType.Service
        ) => (type === WorkRequestType.Service ? 3 : type === WorkRequestType.Preventative ? 1 : 2)

        const updateThreshold = async (
            type: WorkRequestType.Inspection | WorkRequestType.Preventative | WorkRequestType.Service,
            group: ThresholdGroup
        ): Promise<Outcome<ThresholdGroup>> => {
            const method = group.id === null ? HttpMethod.Post : HttpMethod.Put
            const path = group.id === null ? "/core-api/UpcomingThreshold" : `/core-api/UpcomingThreshold/${group.id}`

            const body = {
                ...(method === HttpMethod.Put ? { upcomingThresholdId: group.id } : {}),
                serviceTypeID: serviceIdForGroupType(type),
                hoursUntil: valueForThresholdType(group[ThresholdType.Hours], ThresholdUnit.Unit),
                hoursUntilPercentage: valueForThresholdType(group[ThresholdType.Hours], ThresholdUnit.Percent),
                milesUntil: valueForThresholdType(group[ThresholdType.Miles], ThresholdUnit.Unit),
                milesUntilPercentage: valueForThresholdType(group[ThresholdType.Miles], ThresholdUnit.Percent),
                daysUntil: valueForThresholdType(group[ThresholdType.Days], ThresholdUnit.Unit),
                daysUntilPercentage: valueForThresholdType(group[ThresholdType.Days], ThresholdUnit.Percent),
                applyToSubCompanies: group.applyToSubCompanies,
            }

            const response = await this.apiService.makeRequest({ method, path, body })
            if (response.isError()) return Outcome.error(response)
            return Outcome.ok(thresholdGroupFromObj(response.value))
        }

        const [inspectionResponse, preventativeResponse, serviceResponse] = await Promise.all([
            updateThreshold(WorkRequestType.Inspection, thresholds[WorkRequestType.Inspection]),
            updateThreshold(WorkRequestType.Preventative, thresholds[WorkRequestType.Preventative]),
            updateThreshold(WorkRequestType.Service, thresholds[WorkRequestType.Service]),
        ])

        if (inspectionResponse.isError()) return inspectionResponse
        if (preventativeResponse.isError()) return preventativeResponse
        if (serviceResponse.isError()) return serviceResponse

        return Outcome.ok({
            [WorkRequestType.Inspection]: inspectionResponse.value,
            [WorkRequestType.Preventative]: preventativeResponse.value,
            [WorkRequestType.Service]: serviceResponse.value,
        })
    }

    async fetchServiceCodes(): Promise<Outcome<Record<number, ServiceCode>>> {
        const pathAllResponse = "/core-api/ServiceCode"
        let allResponse = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, pathAllResponse))) {
            allResponse = await this.userCacheModule.getCachedResponse(user, pathAllResponse)
        } else {
            allResponse = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: pathAllResponse,
            })
        }

        if (!ApiService.isValidArrayResponse(allResponse)) return Outcome.error(allResponse)

        const allServiceCodes = arrayToObject(
            allResponse.value.sort((a, b) => a.code.localeCompare(b.code)),
            (it) => [numOrDefault(it.serviceCodeID, 0), serviceCodeFromApi(it)]
        )

        if (user) {
            this.userCacheModule.setCachedResponse(user, pathAllResponse, allResponse)
        }

        const pathNotAssignedToWOResponse = "/core-api/ServiceCode/GetServiceCodesInUse?IsAssignToWorkOrder=false"
        let notAssignedToWOResponse = null

        if (user && (await this.userCacheModule.isValid(user, pathNotAssignedToWOResponse))) {
            notAssignedToWOResponse = await this.userCacheModule.getCachedResponse(user, pathNotAssignedToWOResponse)
        } else {
            notAssignedToWOResponse = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: pathNotAssignedToWOResponse,
            })
        }

        if (!ApiService.isValidArrayResponse(notAssignedToWOResponse)) return Outcome.error(notAssignedToWOResponse)
        notAssignedToWOResponse.value.forEach((it) => {
            const id = numOrDefault(it.serviceCodeID, 0)
            if (allServiceCodes[id] !== undefined) {
                allServiceCodes[id].associatedWithWorkRequest = true
            }
        })

        if (user) {
            this.userCacheModule.setCachedResponse(user, pathNotAssignedToWOResponse, notAssignedToWOResponse)
        }

        const pathAssignedToWOResponse = "/core-api/ServiceCode/GetServiceCodesInUse?IsAssignToWorkOrder=true"
        let assignedToWOResponse = null

        if (user && (await this.userCacheModule.isValid(user, pathAssignedToWOResponse))) {
            assignedToWOResponse = await this.userCacheModule.getCachedResponse(user, pathAssignedToWOResponse)
        } else {
            assignedToWOResponse = await this.apiService.makeRequest({
                method: HttpMethod.Get,
                path: pathAssignedToWOResponse,
            })
        }

        if (!ApiService.isValidArrayResponse(assignedToWOResponse)) return Outcome.error(assignedToWOResponse)
        assignedToWOResponse.value.forEach((it) => {
            const id = numOrDefault(it.serviceCodeID, 0)
            if (allServiceCodes[id] !== undefined) {
                allServiceCodes[id].associatedWithWorkOrder = true
            }
        })

        if (user) {
            this.userCacheModule.setCachedResponse(user, pathAssignedToWOResponse, assignedToWOResponse)
        }
        return Outcome.ok(allServiceCodes)
    }

    async fetchServiceScheduleFilterOptions(): Promise<Outcome<Record<number, ServiceSchedule>>> {
        const response = await this.apiService.makeRequest({
            method: HttpMethod.Post,
            body: {},
            path: "/core-api/ServiceSchedule/GetServiceScheduleFilterOptions",
        })

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)
        const allServiceSchedulesInUse = arrayToObject(response.value, (it) => [
            numOrDefault(it.serviceScheduleID, 0),
            serviceScheduleFromApi(it, true),
        ])

        return Outcome.ok(allServiceSchedulesInUse)
    }

    async fetchServiceSchedules(): Promise<Outcome<Record<number, ServiceSchedule>>> {
        const allResponse = await this.apiService.makeRequest({
            method: HttpMethod.Get,
            path: "/core-api/ServiceSchedule",
        })
        if (!ApiService.isValidArrayResponse(allResponse)) return Outcome.error(allResponse)
        const allServiceSchedules = arrayToObject(allResponse.value, (it) => [
            numOrDefault(it.serviceScheduleID, 0),
            serviceScheduleFromApi(it),
        ])

        const inUseResponse = await this.apiService.makeRequest({
            method: HttpMethod.Get,
            path: "/core-api/ServiceSchedule/GetServiceSchedulesInUse",
        })
        if (!ApiService.isValidArrayResponse(inUseResponse)) return Outcome.error(inUseResponse)
        inUseResponse.value.forEach((it) => {
            const id = numOrDefault(it.serviceScheduleID, 0)
            if (allServiceSchedules[id] !== undefined) allServiceSchedules[id].associatedWithWorkRequest = true
        })

        return Outcome.ok(allServiceSchedules)
    }

    async fetchServiceSchedulesInUse(assetId: number): Promise<Outcome<ServiceSchedule[]>> {
        const inUseResponse = await this.apiService.makeRequest({
            method: HttpMethod.Post,
            path: "/core-api/ServiceSchedule/GetServiceSchedulesInUse",
            body: { assetId: [assetId] },
        })
        if (!ApiService.isValidArrayResponse(inUseResponse)) return Outcome.error(inUseResponse)

        return Outcome.ok(inUseResponse.value.map((it) => serviceScheduleFromApi(it)))
    }

    async fetchServiceScheduleItemsInUse(assetId: number): Promise<Outcome<Step[]>> {
        const inUseResponse = await this.apiService.makeRequest({
            method: HttpMethod.Post,
            path: "/core-api/ServiceSchedule/GetServiceItems",
            body: { assetId: [assetId] },
        })
        if (!ApiService.isValidArrayResponse(inUseResponse)) return Outcome.error(inUseResponse)

        let result = inUseResponse.value.map((it) => this.stepFromApi(it))

        return Outcome.ok(result)
    }

    async fetchServiceScheduleItemsInUseForCompany(companyIds: number[]): Promise<Outcome<Step[]>> {
        const path = "/core-api/ServiceSchedule/GetServiceItems"
        let response: any = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({
                method: HttpMethod.Post,
                path: path,
                body: { companyId: companyIds },
            })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map((it: any) => this.stepFromApi(it)))
    }

    stepFromApi(serviceScheduleStep: any): Step {
        return {
            id: serviceScheduleStep.serviceScheduleItemId,
            step: serviceScheduleStep.step,
            label: serviceScheduleStep.serviceStep ? serviceScheduleStep.serviceStep.name : serviceScheduleStep.step,
            serviceScheduleId: serviceScheduleStep.serviceScheduleId ?? 0,
        }
    }

    async fetchUrgencies(): Promise<Outcome<CompanyUrgency[]>> {
        const path = "/core-api/Urgency"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Get, path: path })
        }

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.map((it) => companyUrgencyFromApi(it)))
    }

    async fetchVendors(companyId: number): Promise<Outcome<ServiceQuoteVendor[], unknown>> {
        const response = await this.apiService.makeRequest<ApiServiceQuote[]>({
            method: HttpMethod.Post,
            path: "/core-api/ServiceQuote/GetServiceQuotes",
            body: { companyId: companyId },
        })

        if (!ApiService.isValidArrayResponse(response)) return Outcome.error(response)

        const uniqueVendorsMap = new Map<number, ServiceQuoteVendor>()

        response.value.forEach((quote) => {
            const vendor = serviceQuoteVendorFromApi(quote.vendor)
            if (vendor && !uniqueVendorsMap.has(vendor.id)) {
                uniqueVendorsMap.set(vendor.id, vendor)
            }
        })

        // Convert the map values to an array
        const vendors = Array.from(uniqueVendorsMap.values())

        return Outcome.ok(vendors)
    }

    async fetchTimeZones(): Promise<Outcome<TimeZone[]>> {
        const path = "/core-api/TimeZone"
        let response = null
        let user = this.apiService.getUser()

        if (user && (await this.userCacheModule.isValid(user, path))) {
            response = await this.userCacheModule.getCachedResponse(user, path)
        } else {
            response = await this.apiService.makeRequest({ method: HttpMethod.Get, path: path })
        }

        if (!ApiService.isValidPagedResponse(response)) return Outcome.error(response)

        if (user) await this.userCacheModule.setCachedResponse(user, path, response)

        return Outcome.ok(response.value.results.map(timeZoneFromObj))
    }
}

function timeZoneFromObj(obj: any): TimeZone {
    return {
        id: numOrDefault(obj.timeZoneId, 0),
        name: stringOrDefault(obj.name, ""),
        StandardName: stringOrDefault(obj.standardName, ""),
        Abbreviation: stringOrDefault(obj.abbreviation, ""),
    }
}

function serviceSubTypeFromResponse(type: ServiceRequestType, obj: any[]): ServiceSubType {
    const nameToFind = ((): string => {
        switch (type) {
            case ServiceRequestType.FollowUp:
                return "Follow-Up"
            case ServiceRequestType.Repair:
                return "Repair"
            case ServiceRequestType.Inspection:
                return "Inspection"
            case ServiceRequestType.Warranty:
                return "Warranty"
        }
    })()

    const item = obj.find((it) => stringOrDefault(it.name, "") === nameToFind)

    return {
        id: numOrDefault(item?.serviceSubTypeID, -1),
        companyId: numOrDefault(item?.companyID, -1),
        alias: nullStringOrDefault(item?.alias, null),
        name: stringOrDefault(item?.name, ""),
        type: type,
    }
}

function groupFromObj(obj: any): Group {
    return {
        id: numOrDefault(obj.groupID, 0),
        name: stringOrDefault(obj.name, ""),
        companyId: numOrDefault(obj.companyID, 0),
    }
}

function districtFromObj(obj: any): District {
    return {
        id: numOrDefault(obj.districtID, 0),
        parentId: nullNumOrDefault(obj.parentDistrictID, null),
        name: stringOrDefault(obj.districtName, ""),
        companyId: numOrDefault(obj.companyID, 0),
    }
}

function thresholdGroupFromObj(obj: any): ThresholdGroup {
    if (obj === undefined || obj === null) return newThresholdGroup()
    const dayUnit = obj.daysUntilPercentage !== null ? ThresholdUnit.Percent : ThresholdUnit.Unit
    const mileUnit = obj.milesUntilPercentage !== null ? ThresholdUnit.Percent : ThresholdUnit.Unit
    const hourUnit = obj.hoursUntilPercentage !== null ? ThresholdUnit.Percent : ThresholdUnit.Unit
    return {
        id: numOrDefault(obj.upcomingThresholdId, 0),
        [ThresholdType.Days]: {
            unit: dayUnit,
            value: nullNumOrDefault(dayUnit === ThresholdUnit.Unit ? obj.daysUntil : obj.daysUntilPercentage, null),
        },
        [ThresholdType.Hours]: {
            unit: hourUnit,
            value: nullNumOrDefault(hourUnit === ThresholdUnit.Unit ? obj.hoursUntil : obj.hoursUntilPercentage, null),
        },
        [ThresholdType.Miles]: {
            unit: mileUnit,
            value: nullNumOrDefault(mileUnit === ThresholdUnit.Unit ? obj.milesUntil : obj.milesUntilPercentage, null),
        },
        applyToSubCompanies: boolOrDefault(obj.applyToSubCompanies, false),
        isReadOnly: boolOrDefault(obj.isReadOnly, false),
    }
}

function serviceCodeFromApi(obj: any): ServiceCode {
    return {
        id: numOrDefault(obj.serviceCodeID, 0),
        name: stringOrDefault(obj.name, ""),
        code: stringOrDefault(obj.code, ""),
        companyId: numOrDefault(obj.companyID, 0),
        associatedWithWorkRequest: false,
        associatedWithWorkOrder: false,
    }
}

function serviceScheduleFromApi(obj: any, associatedWithWorkRequest: boolean = false): ServiceSchedule {
    return {
        id: numOrDefault(obj.serviceScheduleID, 0),
        name: stringOrDefault(obj.name, ""),
        serviceScheduleType: serviceScheduleTypeFromApi(obj.serviceScheduleTypeId),
        days: numOrDefault(obj.days, 0),
        hourMeter: numOrDefault(obj.hourMeter, 0),
        odometer: numOrDefault(obj.odometer, 0),
        targetDate: nullableDateFromApi(obj.targetDate),
        companyId: numOrDefault(obj.companyID, 0),
        associatedWithWorkRequest: associatedWithWorkRequest,
    }
}

function serviceScheduleTypeFromApi(obj: any): ServiceScheduleType | null {
    switch (obj) {
        case null:
            return null
        case 1:
            return ServiceScheduleType.Preventative
        case 2:
            return ServiceScheduleType.Inspection
    }
    throw new Error(`Invalid schedule type ${obj}`)
}

function serviceScheduleTypeToApi(type: ServiceScheduleType | null): number | null {
    if (type === null) return null
    switch (type) {
        case ServiceScheduleType.Preventative:
            return 1
        case ServiceScheduleType.Inspection:
            return 2
    }
}

function siteFromObj(obj: any): Site {
    return {
        id: numOrDefault(obj.siteId, 0),
        name: stringOrDefault(obj.name, ""),
        description: stringOrDefault(obj.description, ""),
        companyId: numOrDefault(obj.companyId, -1),
    }
}

function contactFromApi(obj: any): Contact {
    return {
        id: numOrDefault(obj.personId, 0),
        firstName: nullStringOrDefault(obj.firstName, null),
        lastName: nullStringOrDefault(obj.lastName, null),
        displayName: nullStringOrDefault(obj.displayName, null),
        email: nullStringOrDefault(obj.email, null),
        isMechanic: obj.mechanic === true,
        companyName: nullStringOrDefault(obj.companyName, null),
        company: {
            id: numOrDefault(obj.company ? obj.company.companyId : obj.companyId, 0),
            name: nullStringOrDefault(obj.company ? obj.company.companyName : obj.companyName, null),
        },
        homePhone: nullStringOrDefault(obj.homePhone, null),
        officePhone: nullStringOrDefault(obj.officePhone, null),
        cellPhone: nullStringOrDefault(obj.cellPhone, null),
        title: nullStringOrDefault(obj.title, null),
        notes: nullStringOrDefault(obj.notes, null),
        address: nullStringOrDefault(obj.address, null),
        city: nullStringOrDefault(obj.city, null),
        state: nullNumOrDefault(obj.stateID, null),
        postalCode: nullStringOrDefault(obj.postalCode, null),
        personTypeID: nullNumOrDefault(obj.personTypeID, null),
        hasActiveWorkOrders: boolOrDefault(obj.hasActiveWorkOrders, false),
        deleted: boolOrDefault(obj.deleted, false),
    }
}

function CBACodeFromApi(obj: any): CBACode {
    return {
        code: obj.code,
        name: obj.name,
        cbaCodeId: obj.cbaCodeID,
    }
}

function contactToApi(contact: Contact): any {
    return {
        personId: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        displayName: contact.displayName,
        email: contact.email,
        mechanic: contact.isMechanic,
        companyID: contact.company.id,
        homePhone: contact.homePhone,
        officePhone: contact.officePhone,
        cellPhone: contact.cellPhone,
        companyName: contact.companyName,
        title: contact.title,
        notes: contact.notes,
        address: contact.address,
        city: contact.city,
        stateID: contact.state,
        postalCode: contact.postalCode,
    }
}

function companyUrgencyFromApi(obj: any): CompanyUrgency {
    return {
        type: (() => {
            switch (stringOrDefault(obj.name, "Medium")) {
                case "Immediate":
                    return Urgency.Immediate
                case "High":
                    return Urgency.High
                case "Medium":
                    return Urgency.Medium
                case "Low":
                    return Urgency.Low
                default:
                    return Urgency.Medium
            }
        })(),
        companyId: numOrDefault(obj.companyId, 0),
        id: numOrDefault(obj.urgencyID, 0),
    }
}

function workOrderAutomationFromApi(obj: any): WorkOrderAutomationSettings {
    return {
        id: numOrDefault(obj.workOrderAutomationSettingsID, 0),
        companyId: numOrDefault(obj.companyId, 0),
        [WorkRequestType.Inspection]: boolOrDefault(obj.inspection, false),
        [WorkRequestType.Preventative]: boolOrDefault(obj.preventativeMaintenance, false),
        [WorkRequestType.Service]: boolOrDefault(obj.serviceRequest, false),
        applyToSubCompanies: boolOrDefault(obj.applyToSubCompanies, false),
        isReadOnly: boolOrDefault(obj.isReadOnly, false),
    }
}

function workOrderRequiredFieldsFromApi(obj: any): WorkOrderRequiredFields {
    const workOrderFields = obj?.workOrderFields ?? {}
    const serviceItemFields = obj?.serviceItemFields ?? {}

    return {
        companyId: numOrDefault(obj?.companyId, 0),
        workOrderFields: {
            assignedTo: boolOrDefault(workOrderFields.assignedTo, false),
            site: boolOrDefault(workOrderFields.site, false),
        },
        serviceItemFields: {
            workPerformedBy: boolOrDefault(serviceItemFields.workPerformedBy, false),
            serviceCode: boolOrDefault(serviceItemFields.serviceCode, false),
            laborHours: boolOrDefault(serviceItemFields.laborHours, false),
            laborCost: boolOrDefault(serviceItemFields.laborCost, false),
            partsCost: boolOrDefault(serviceItemFields.partsCost, false),
            miscCost: boolOrDefault(serviceItemFields.miscCost, false),
            mechanicsNotes: boolOrDefault(serviceItemFields.mechanicsNotes, false),
            measurements: boolOrDefault(serviceItemFields.measurements, false),
            attachments: boolOrDefault(serviceItemFields.attachments, false),
        },
        isReadOnly: boolOrDefault(obj?.isReadOnly, false),
    }
}

// companyId and isReadOnly are server-owned and deliberately omitted from the request body.
function workOrderRequiredFieldsToApi(requiredFields: WorkOrderRequiredFields): any {
    return {
        workOrderFields: { ...requiredFields.workOrderFields },
        serviceItemFields: { ...requiredFields.serviceItemFields },
    }
}

function workOrderAutomationToApi(settings: WorkOrderAutomationSettings): any {
    return {
        ...(settings.id === null ? { workOrderAutomationSettingsID: settings.id } : {}),
        ...(settings.id === null ? { companyId: settings.companyId } : {}),
        preventativeMaintenance: settings[WorkRequestType.Preventative],
        inspection: settings[WorkRequestType.Inspection],
        serviceRequest: settings[WorkRequestType.Service],
        applyToSubCompanies: settings.applyToSubCompanies,
    }
}

function serviceQuoteVendorFromApi(obj: ApiServiceQuoteVendor | null): ServiceQuoteVendor | null {
    if (obj == null) {
        return null
    }
    return {
        id: numOrDefault(obj.vendorId, 0),
        VendorName: obj.vendorName,
        Description: obj.description,
        PrimaryContactEmail: obj.primaryContactEmail,
        PrimaryContactName: obj.primaryContactName,
        PrimaryContactPhoneNumber: obj.primaryContactPhoneNumber,
        PrimaryResponseEmailAddress: obj.primaryResponseEmailAddress,
    }
}
